import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';
import { httpClient } from "../../common/httpclient";
import PageScaffold from "../../component/pageScaffold/PageScaffold";
import SelectorClient from "../service_order/selectorClient";
import TextAreaWithCounter from '../../component/input/TextAreaWithCounter';
import StatusChip from '../../component/common/StatusChip';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CalendarClock, User, PenLine, Eye, Check, ChevronDown } from 'lucide-react';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { KitButton } from '../../component/common/MobileKit';
import { KitField, KitFormBar, KitInput, KitTextarea } from '../../component/common/MobileKitExtras';

// `<input type="date">` <-> dayjs. Troca só o dia, mantendo a hora que já estava no valor.
const toDateInput = (value) => {
    if (!value) return '';
    const date = typeof value.isValid === 'function' ? value : dayjs(value);
    return date.isValid() ? date.format('YYYY-MM-DD') : '';
};
const fromDateInput = (text, previous) => {
    if (!text) return null;
    const parsed = dayjs(text, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) return null;
    return previous && typeof previous.isValid === 'function' && previous.isValid()
        ? parsed.hour(previous.hour()).minute(previous.minute())
        : parsed;
};

dayjs.extend(utc);

/**
 * Formulário de "Novo orçamento" (`/servicequotes/generate`) — mesmo padrão
 * acordeão + preview ao vivo (DESIGN-SYSTEM.md §5.5 "Accordion insertion
 * form" / §6.2) já usado pela Nova Ordem de Serviço
 * (`pages/service_order/Genereate.js`). Ref: `prototipo-novo-orcamento.html`
 * (reaproveita 1:1 o acordeão + preview de `prototipo-ordem-servico.html`,
 * tela `view-nova`).
 *
 * Este componente é standalone: não reaproveita `quoteData.js` (que continua
 * servindo, inalterado, o drawer de edição rápida usado por `Operator.js`/
 * `View.js`/`EditQuoteModal.js`) — mesma decisão já tomada para a Nova Ordem
 * (`Genereate.js` vs `orderData.js`). A lógica de formik/validação/submit
 * abaixo é uma cópia fiel do modo de criação que já existia em
 * `quoteData.js` (`isForm=true`/`editMode=false`), só reorganizada na nova
 * estrutura visual de 3 seções — endpoint (`POST /servicequotes`), mensagens
 * de sucesso/erro e redirecionamento pós-criação (`/servicequotes/:id/operator`)
 * preservados 1:1.
 */

// `ff-textarea` (DESIGN-SYSTEM.md §5.5 A) — mesma cópia local do padrão já
// estabelecido em `orderData.js`/`pages/service_order/Genereate.js`.
const ffFieldSx = {
    '& .MuiInputBase-input': { fontSize: '13.5px' },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        '&.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: '96px',
            alignItems: 'flex-start',
            padding: '24px 14px 7px 14px',
        },
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiInputBase-multiline textarea': {
        resize: 'vertical',
    },
    '& .MuiInputLabel-root': {
        fontSize: '13.5px',
        color: 'text.disabled',
        transform: 'translate(14px, 17px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '10px',
        fontWeight: 800,
        color: 'primary.main',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        transform: 'translate(14px, 8px) scale(1)',
    },
};

// `DateTimePicker` (MUI X) usa um namespace de classes próprio
// (`MuiPickersOutlinedInput-*`/`MuiPickersSectionList-*`), diferente de
// `MuiOutlinedInput-*` — os `styleOverrides` genéricos do tema não o
// alcançam. Mesmo tamanho `ff-input` (52px, radius 12px, fundo
// `background.subtle`) dos demais campos deste formulário.
const ffPickerSx = {
    // Label do `.ff-label`: repouso 13.5px, encolhido 10px/800 uppercase
    // dentro da caixa (sem isso o `MuiInputLabel` cai no default do tema, que
    // cruza a borda porque o `legend` é removido abaixo).
    '& .MuiInputLabel-root': {
        fontSize: '13.5px',
        color: 'text.disabled',
        transform: 'translate(14px, 17px) scale(1)',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '10px',
        fontWeight: 800,
        color: 'primary.main',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        transform: 'translate(14px, 8px) scale(1)',
    },
    '& .MuiPickersOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        height: '52px',
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    '& .MuiPickersOutlinedInput-notchedOutline legend': {
        display: 'none',
    },
    '& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer': {
        fontSize: '13.5px',
        // Mesmo `padding: 21px 14px 7px` do `<input>` do `ff-input` (o container
        // de seções é centralizado na caixa de 52px, então o valor precisa da
        // mesma reserva no topo para não encostar no label encolhido). O padrão
        // do MUI X é `16.5px 0`, que deixava o valor sobre o label.
        padding: '21px 0 7px',
    },
};

// `<span class="ff-req">*</span>` do protótipo.
const requiredFieldLabel = (text) => (
    <>
        {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
    </>
);

/**
 * Seção colapsável do acordeão (`acc-section`/`toggleAcc`/`openAcc` do
 * protótipo). Componente de módulo — fora do render de `Generate` — para não
 * perder o foco dos inputs a cada re-render (mesma nota de
 * `pages/service_order/Genereate.js`/`pages/products/Form.js`).
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 2000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

// `.preview-row` do card de resumo (`ov-card`).
function PreviewRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: 'text.primary', fontWeight: 700, textAlign: 'right' }}>{value}</Box>
        </Box>
    );
}

export default function Generate() {
    const theme = useTheme();
    const navigate = useNavigate();
    // Pré-preenchimento vindo da visualização do cliente (`location.state.prefill`).
    const location = useLocation();
    const prefillCustomerId = location.state?.prefill?.customerId ? String(location.state.prefill.customerId) : '';
    const isMobileShell = useIsMobileShell();

    // Largura real do menu lateral — usada para alinhar o rodapé de ações
    // fixo (`.action-footer` do protótipo) com a borda do conteúdo em vez da
    // borda da viewport. Ref: DESIGN-SYSTEM.md §3.1, mesmo padrão de
    // `pages/service_order/Genereate.js`.
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const { settings, isLoading: configLoading } = useEnterpriseConfigContext();
    const [useVehicle, setUseVehicle] = useState(false);

    const [message, setMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(['dados']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));
    const [selectedCustomer, setSelectedCustomer] = useState(prefillCustomerId);
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [previewInfo, setPreviewInfo] = useState({ customerName: '', customerPhone: '', vehicleLabel: '' });

    useEffect(() => {
        if (settings) {
            setUseVehicle(!!settings.os_use_vehicle);
        }
    }, [settings]);

    // Valores iniciais estáveis para as datas (mesma abordagem de
    // `pages/service_order/Genereate.js`, evita recriar novas instâncias
    // dayjs a cada render). "Válido até" default de +7 dias, mesmo valor
    // sugerido no protótipo.
    const initialDateValues = useMemo(() => ({
        quote_date: dayjs(),
        valid_until: dayjs().add(7, 'day'),
    }), []);

    // Validação: `customer_id` já era obrigatório em `quoteData.js`. As duas
    // datas (data do orçamento + validade) passam a ser obrigatórias nesta
    // tela — "Válido até" é um campo novo de UX proposto pelo redesign, ver
    // nota no formulário e no `onSubmit` abaixo.
    const validationSchema = useMemo(() => Yup.object().shape({
        customer_id: Yup.string().required('Cliente é obrigatório'),
        quote_date: Yup.mixed().nullable().required('Data é obrigatória').test('is-valid-date', 'Data inválida', (value) => {
            if (!value) return false;
            const date = value && typeof value.isValid === 'function' ? value : dayjs(value);
            return date && date.isValid && date.isValid();
        }),
        valid_until: Yup.mixed().nullable().required('Validade é obrigatória').test('is-valid-date', 'Data inválida', (value) => {
            if (!value) return false;
            const date = value && typeof value.isValid === 'function' ? value : dayjs(value);
            return date && date.isValid && date.isValid();
        }),
        observations: Yup.string().optional(),
        defect_reason: Yup.string().optional(),
    }), []);

    const handleRedirect = (id) => {
        if (id) {
            navigate(`/servicequotes/${id}/operator`, { state: { id } });
        }
    };

    const handleSubmit = (values) => {
        setIsLoading(true);

        httpClient.post('/servicequotes', values)
            .then(response => {
                setIsLoading(false);
                if (response.status === 201) {
                    setMessage('Orçamento criado com sucesso');
                    setOpenSnackbar(true);
                    handleRedirect(response.data.id);
                } else {
                    setMessage('Erro ao criar orçamento');
                    setOpenSnackbar(true);
                }
            })
            .catch(error => {
                console.error('Error creating quote:', error);
                setIsLoading(false);
                setMessage('Erro ao criar orçamento: ' + (error.response?.data || error.message));
                setOpenSnackbar(true);
            });
    };

    const formik = useFormik({
        initialValues: {
            customer_id: prefillCustomerId,
            quote_date: initialDateValues.quote_date,
            valid_until: initialDateValues.valid_until,
            observations: '',
            defect_reason: '',
            vehicle_id: '',
        },
        validateOnMount: false,
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema,
        onSubmit: (values) => {
            // Formatar a data para o formato correto (YYYY-MM-DDTHH:mm) em
            // UTC — mesma lógica de `quoteData.js` (mantém a data/hora "como
            // digitada", não o instante UTC real).
            const formatDate = (dateValue) => {
                if (!dateValue) return '';
                const date = dateValue && typeof dateValue.isValid === 'function' ? dateValue : dayjs(dateValue);
                if (!date || !date.isValid || !date.isValid()) return '';
                const utcDate = dayjs.utc(date.format('YYYY-MM-DD HH:mm'));
                return utcDate.format('YYYY-MM-DDTHH:mm');
            };

            // Payload nos mesmos campos do `QuoteInput` da API
            // (`fv-api/servicequote/types.go`): customer_id/vehicle_id/
            // quote_date/observations. `valid_until` fica fora do payload —
            // é um campo novo só de UI (ver hint da seção 1); a API não tem
            // coluna equivalente hoje, e tanto `Creator.Create` quanto
            // `Updater.Update` (`fv-api/servicequote/creator.go`/
            // `updater.go`) já ignoram `quote_date` de qualquer forma
            // (sempre gravam `time.Now()`), então nenhum comportamento de
            // back-end muda com esta tela.
            const formattedValues = {
                customer_id: values.customer_id,
                vehicle_id: values.vehicle_id,
                quote_date: formatDate(values.quote_date),
                observations: values.observations,
                defect_reason: values.defect_reason,
            };

            handleSubmit(formattedValues);
        },
    });

    const handleCustomerSelect = (customerId) => {
        formik.setFieldValue('customer_id', customerId);
        formik.setFieldTouched('customer_id', true);
        setSelectedCustomer(customerId);

        setTimeout(() => {
            formik.validateField('customer_id');
        }, 100);
    };

    const handleVehicleSelect = (event) => {
        const vehicleId = event.target.value;
        formik.setFieldValue('vehicle_id', vehicleId);
        formik.setFieldTouched('vehicle_id', true);
        setSelectedVehicle(vehicleId);
    };

    // Identidade estável (não recriada a cada render) — evita loop de efeito
    // em `SelectorClient` (ver comentário lá).
    const handleSelectionInfoChange = useCallback((info) => {
        setPreviewInfo(info);
    }, []);

    // ---- Acordeão + preview ao vivo: helpers de progresso (mesmo padrão de
    // `pages/service_order/Genereate.js`, DESIGN-SYSTEM.md §5.5/§6.2) ----
    const isDateFilled = (value) => {
        if (!value) return false;
        return typeof value.isValid === 'function' ? value.isValid() : dayjs(value).isValid();
    };
    const isFieldFilled = (fieldName) => {
        if (fieldName === 'quote_date' || fieldName === 'valid_until') {
            return isDateFilled(formik.values[fieldName]);
        }
        const value = formik.values[fieldName];
        if (value === null || value === undefined) return false;
        return String(value).trim().length > 0;
    };

    const sections = [
        {
            key: 'dados',
            title: 'Dados do orçamento',
            subtitle: 'Data do orçamento e validade da proposta',
            icon: CalendarClock,
            requiredFields: ['quote_date', 'valid_until'],
        },
        {
            key: 'cliente',
            title: useVehicle ? 'Cliente e veículo' : 'Cliente',
            subtitle: useVehicle ? 'Quem é o cliente e qual veículo será orçado' : 'Quem é o cliente que será atendido',
            icon: User,
            requiredFields: ['customer_id'],
        },
        {
            key: 'obs',
            title: 'Observações',
            subtitle: 'Opcional — relato do cliente, condições da proposta',
            icon: PenLine,
            requiredFields: [],
        },
    ];
    const requiredSections = sections.filter((sec) => sec.requiredFields.length > 0);
    const completeSectionsCount = requiredSections.filter((sec) => sec.requiredFields.every(isFieldFilled)).length;
    const progressPct = requiredSections.length > 0
        ? Math.round((completeSectionsCount / requiredSections.length) * 100)
        : 0;
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    // Mapa campo → seção, usado só para reabrir automaticamente a seção com
    // erro ao tentar gerar o orçamento (mesmo padrão de
    // `pages/service_order/Genereate.js`).
    const fieldSectionMap = { quote_date: 'dados', valid_until: 'dados', customer_id: 'cliente' };
    const handleGenerateClick = async () => {
        const errors = await formik.validateForm();
        const errorFieldKeys = Object.keys(errors);
        if (errorFieldKeys.length > 0) {
            const touchedFields = {};
            Object.keys(formik.values).forEach((key) => { touchedFields[key] = true; });
            formik.setTouched(touchedFields);

            const sectionKeyWithError = errorFieldKeys.map((key) => fieldSectionMap[key]).find(Boolean);
            if (sectionKeyWithError) {
                ensureSectionOpen(sectionKeyWithError);
            }
            return;
        }
        formik.handleSubmit();
    };

    const formatPreviewDate = (value) => {
        if (!isDateFilled(value)) return '—';
        const date = typeof value.isValid === 'function' ? value : dayjs(value);
        return date.format('DD/MM/YYYY HH:mm');
    };

    const customerInitials = previewInfo.customerName
        ? previewInfo.customerName.trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join('')
        : '?';

    const breadcrumbs = [
        { title: 'Orçamentos de Serviço', link: '/servicequotes' },
    ];

    if (configLoading) {
        return (
            <PageScaffold
                title="Novo Orçamento de Serviço"
                titlePageTarget={'Novo'}
                links={breadcrumbs}
                openSnackbar={openSnackbar}
                message={message}
                setOpenSnackbar={setOpenSnackbar}
                severity="success"
            >
                <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', p: 2 }}>
                    <Skeleton variant="rounded" height={40} width={320} sx={{ mb: 3 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                        <Stack spacing={1.75}>
                            <Skeleton variant="rounded" height={140} sx={{ borderRadius: '18px' }} />
                            <Skeleton variant="rounded" height={140} sx={{ borderRadius: '18px' }} />
                            <Skeleton variant="rounded" height={140} sx={{ borderRadius: '18px' }} />
                        </Stack>
                        <Skeleton variant="rounded" height={280} sx={{ borderRadius: '20px' }} />
                    </Box>
                </Box>
            </PageScaffold>
        );
    }

    // Shell mobile: formulário do protótipo `orcamentos/novo.html` (mesmos campos, validação e envio).
    if (isMobileShell) {
        const dateError = (field) => formik.touched[field] && Boolean(formik.errors[field]);
        return (
            <PageScaffold
                title="Novo Orçamento de Serviço"
                titlePageTarget={'Novo'}
                links={breadcrumbs}
                openSnackbar={openSnackbar}
                message={message}
                setOpenSnackbar={setOpenSnackbar}
                severity="success"
            >
                <Box
                    component="form"
                    noValidate
                    onSubmit={(e) => { e.preventDefault(); handleGenerateClick(); }}
                    sx={{ fontSize: 14, lineHeight: 1.4 }}
                >
                    {/* `.wizard` + `.wiz-label` */}
                    <Box sx={{ display: 'flex', gap: '6px', mb: '6px' }}>
                        <Box component="span" sx={{ flex: 1, height: 4, borderRadius: '2px', backgroundColor: 'primary.main' }} />
                        <Box component="span" sx={{ flex: 1, height: 4, borderRadius: '2px', backgroundColor: 'divider' }} />
                    </Box>
                    <Box sx={{ fontSize: 12, color: 'text.disabled', fontWeight: 600, mb: '14px' }}>Passo 1 de 2 · Dados do orçamento</Box>

                    <SelectorClient
                        mobile
                        formik={formik}
                        selectedCustomer={selectedCustomer}
                        handleChange={handleCustomerSelect}
                        selectedVehicle={selectedVehicle}
                        handleVehicleChange={handleVehicleSelect}
                        useVehicle={useVehicle}
                        onSelectionInfoChange={handleSelectionInfoChange}
                    />

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                        <KitField label="Data" error={dateError('quote_date')} help={dateError('quote_date') ? formik.errors.quote_date : undefined}>
                            <KitInput
                                type="date"
                                aria-label="Data do orçamento"
                                value={toDateInput(formik.values.quote_date)}
                                error={dateError('quote_date')}
                                onChange={(e) => {
                                    formik.setFieldValue('quote_date', fromDateInput(e.target.value, formik.values.quote_date));
                                    formik.setFieldTouched('quote_date', true);
                                }}
                            />
                        </KitField>
                        <KitField label="Validade" error={dateError('valid_until')} help={dateError('valid_until') ? formik.errors.valid_until : undefined}>
                            <KitInput
                                type="date"
                                aria-label="Validade do orçamento"
                                value={toDateInput(formik.values.valid_until)}
                                error={dateError('valid_until')}
                                onChange={(e) => {
                                    formik.setFieldValue('valid_until', fromDateInput(e.target.value, formik.values.valid_until));
                                    formik.setFieldTouched('valid_until', true);
                                }}
                            />
                        </KitField>
                    </Box>

                    <KitField label="Observações">
                        <KitTextarea
                            name="observations"
                            maxLength={500}
                            placeholder="Condições, garantia, prazos"
                            value={formik.values.observations}
                            onChange={formik.handleChange}
                        />
                    </KitField>
                    <KitField label="Motivo / defeito">
                        <KitTextarea
                            name="defect_reason"
                            maxLength={500}
                            placeholder="Relato do cliente, defeito apresentado"
                            value={formik.values.defect_reason}
                            onChange={formik.handleChange}
                        />
                    </KitField>

                    <KitFormBar>
                        <KitButton onClick={() => navigate('/servicequotes')} disabled={isLoading}>Cancelar</KitButton>
                        <KitButton variant="primary" onClick={handleGenerateClick} disabled={isLoading}>
                            {isLoading ? 'Gerando…' : 'Continuar'}
                        </KitButton>
                    </KitFormBar>
                </Box>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Novo Orçamento de Serviço"
            titlePageTarget={'Novo'}
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            message={message}
            setOpenSnackbar={setOpenSnackbar}
            severity="success"
        >
            <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto' }}>
                {/* `form-hero` — título + descrição + anel de progresso, igual a
                    `prototipo-novo-orcamento.html` e ao mesmo padrão usado em
                    `pages/service_order/Genereate.js` (Nova Ordem). */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '20px',
                        flexWrap: 'wrap',
                        mb: 2.25,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                            Novo Orçamento
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                            Preencha por seções — o card ao lado mostra o resumo do orçamento em tempo real. Os itens são adicionados na tela de operação, após gerar.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                        <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                            <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                <circle
                                    cx="27"
                                    cy="27"
                                    r={RING_R}
                                    fill="none"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    stroke={theme.palette.primary.main}
                                    strokeDasharray={RING_CIRC}
                                    strokeDashoffset={ringOffset}
                                    style={{ transition: 'stroke-dashoffset .35s ease' }}
                                />
                            </svg>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    fontWeight: 800,
                                }}
                            >
                                {progressPct}%
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Novo orçamento</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                completo conforme você preenche
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* `step-pills` */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.25 }}>
                    {sections.map((sec, idx) => {
                        const isSecOpen = isSectionOpen(sec.key);
                        const isSecComplete = sec.requiredFields.length > 0 && sec.requiredFields.every(isFieldFilled);
                        return (
                            <Box
                                key={sec.key}
                                onClick={() => ensureSectionOpen(sec.key)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    px: '14px',
                                    py: '8px',
                                    borderRadius: '24px',
                                    border: '1.5px solid',
                                    borderColor: isSecOpen ? 'primary.main' : 'divider',
                                    backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                    color: isSecOpen ? 'primary.main' : 'text.secondary',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 17,
                                        height: 17,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: isSecComplete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                        backgroundColor: isSecComplete ? 'success.main' : 'transparent',
                                        color: isSecComplete ? '#fff' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 9,
                                        flexShrink: 0,
                                    }}
                                >
                                    {isSecComplete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                </Box>
                                {sec.title}
                            </Box>
                        );
                    })}
                </Box>

                {/* `form-columns` — acordeão à esquerda + `preview-sticky` à direita */}
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                        <Stack spacing={1.75} sx={{ minWidth: 0 }}>
                            <AccordionSection
                                open={isSectionOpen('dados')}
                                complete={sections[0].requiredFields.every(isFieldFilled)}
                                icon={CalendarClock}
                                title="Dados do orçamento"
                                subtitle="Data do orçamento e validade da proposta"
                                onToggle={() => toggleSection('dados')}
                            >
                                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                                        <DateTimePicker
                                            label={requiredFieldLabel('Data e hora do orçamento')}
                                            format="DD/MM/YYYY HH:mm"
                                            value={formik.values.quote_date}
                                            onChange={(newValue) => {
                                                formik.setFieldValue('quote_date', newValue);
                                                formik.setFieldTouched('quote_date', true);
                                            }}
                                            slotProps={{
                                                textField: {
                                                    variant: 'outlined',
                                                    size: 'medium',
                                                    fullWidth: true,
                                                    error: formik.touched.quote_date && Boolean(formik.errors.quote_date),
                                                    helperText: formik.touched.quote_date && formik.errors.quote_date,
                                                    sx: ffPickerSx,
                                                },
                                            }}
                                        />
                                        <DateTimePicker
                                            label={requiredFieldLabel('Válido até')}
                                            format="DD/MM/YYYY HH:mm"
                                            value={formik.values.valid_until}
                                            onChange={(newValue) => {
                                                formik.setFieldValue('valid_until', newValue);
                                                formik.setFieldTouched('valid_until', true);
                                            }}
                                            slotProps={{
                                                textField: {
                                                    variant: 'outlined',
                                                    size: 'medium',
                                                    fullWidth: true,
                                                    error: formik.touched.valid_until && Boolean(formik.errors.valid_until),
                                                    helperText: formik.touched.valid_until && formik.errors.valid_until,
                                                    sx: ffPickerSx,
                                                },
                                            }}
                                        />
                                    </Box>
                                </LocalizationProvider>
                                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1.5 }}>
                                    A validade ainda não é salva pela API (hoje só existe uma data única de orçamento) — por enquanto é usada apenas como referência visual neste formulário e no resumo ao lado.
                                </Typography>
                            </AccordionSection>

                            <AccordionSection
                                open={isSectionOpen('cliente')}
                                complete={sections[1].requiredFields.every(isFieldFilled)}
                                icon={User}
                                title={useVehicle ? 'Cliente e veículo' : 'Cliente'}
                                subtitle={useVehicle ? 'Quem é o cliente e qual veículo será orçado' : 'Quem é o cliente que será atendido'}
                                onToggle={() => toggleSection('cliente')}
                            >
                                <SelectorClient
                                    formik={formik}
                                    selectedCustomer={selectedCustomer}
                                    handleChange={handleCustomerSelect}
                                    selectedVehicle={selectedVehicle}
                                    handleVehicleChange={handleVehicleSelect}
                                    useVehicle={useVehicle}
                                    onSelectionInfoChange={handleSelectionInfoChange}
                                />
                            </AccordionSection>

                            <AccordionSection
                                open={isSectionOpen('obs')}
                                complete={false}
                                icon={PenLine}
                                title="Observações"
                                subtitle="Opcional — relato do cliente, condições da proposta"
                                onToggle={() => toggleSection('obs')}
                            >
                                <Stack spacing={2}>
                                <TextAreaWithCounter
                                    label="Motivo/Defeito (opcional)"
                                    maxLength={500}
                                    name="defect_reason"
                                    value={formik.values.defect_reason}
                                    handleChange={formik.handleChange}
                                    error={formik.touched.defect_reason && Boolean(formik.errors.defect_reason)}
                                    helperText={formik.touched.defect_reason && formik.errors.defect_reason}
                                    variant="outlined"
                                    rows={3}
                                    sx={ffFieldSx}
                                    captionText="Descreva o motivo da entrada ou o defeito relatado"
                                />
                                <TextAreaWithCounter
                                    label="Observações sobre o orçamento (opcional)"
                                    maxLength={500}
                                    name="observations"
                                    value={formik.values.observations}
                                    handleChange={formik.handleChange}
                                    error={formik.touched.observations && Boolean(formik.errors.observations)}
                                    helperText={formik.touched.observations && formik.errors.observations}
                                    variant="outlined"
                                    rows={3}
                                    sx={ffFieldSx}
                                />
                                </Stack>
                            </AccordionSection>
                        </Stack>

                        {/* `preview-sticky` — resumo do orçamento ao vivo (`ov-card`),
                            status pill "Novo" (`StatusChip domain="serviceQuote"`,
                            mesmo domínio de `prototipo-lista-orcamentos.html`). */}
                        <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                            <Typography
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '10.5px',
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.06em',
                                    color: 'text.disabled',
                                    mb: 1.25,
                                }}
                            >
                                <Eye size={13} /> Resumo do orçamento
                            </Typography>

                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: `${theme.customRadius.hero}px`,
                                    backgroundColor: 'background.paper',
                                    boxShadow: theme.customShadows.md,
                                    p: '20px',
                                    mb: 1.75,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', mb: 2 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                                        Orçamento a gerar
                                    </Typography>
                                    <StatusChip domain="serviceQuote" value="Novo" />
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            color: '#fff',
                                            fontSize: 13,
                                            fontWeight: 800,
                                            background: theme.palette.gradient.brand,
                                        }}
                                    >
                                        {customerInitials}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 800 }} noWrap>
                                            {previewInfo.customerName || 'Selecione um cliente'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }} noWrap>
                                            {useVehicle
                                                ? (previewInfo.vehicleLabel || 'Nenhum veículo selecionado')
                                                : (previewInfo.customerPhone || '—')}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ height: '1px', backgroundColor: 'divider', my: '14px' }} />

                                <PreviewRow label="Data do orçamento" value={formatPreviewDate(formik.values.quote_date)} />
                                <PreviewRow label="Válido até" value={formatPreviewDate(formik.values.valid_until)} />
                                <PreviewRow label="Motivo/Defeito" value={formik.values.defect_reason?.trim() || '—'} />
                                <PreviewRow label="Observações" value={formik.values.observations?.trim() || '—'} />
                            </Box>

                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Espaçador para compensar o rodapé fixo abaixo. */}
                    <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                    {/* `action-footer` — barra fixa na base da viewport (não um
                        card flutuante no rodapé do acordeão). `left` acompanha a
                        largura real do menu lateral, inclusive recolhido/mobile. */}
                    <Box
                        sx={{
                            position: 'fixed',
                            left: actionFooterLeft,
                            right: 0,
                            bottom: 0,
                            zIndex: theme.zIndex.appBar,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            backgroundColor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                            px: 3,
                            py: 1.75,
                            pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                            pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                            pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                            transition: 'left .2s ease',
                        }}
                    >
                        <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                            Campos com * são obrigatórios
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                            <Button variant="outlined" onClick={() => navigate('/servicequotes')} disabled={isLoading}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Check size={15} strokeWidth={2.5} />}
                                onClick={handleGenerateClick}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Gerando orçamento...' : 'Gerar orçamento'}
                            </Button>
                        </Stack>
                    </Box>
                </form>
            </Box>
        </PageScaffold>
    );
}
